import * as dayjs from 'dayjs';
import { Decimal } from 'decimal.js';
import { Component, ReactNode } from 'react';
import { Calendar, dayjsLocalizer, View } from 'react-big-calendar';
import Modal from 'react-bootstrap/Modal';
import { useNavigate, useParams, useSearchParams } from 'react-router';
import { AdminApi } from '../admin-api';
import Header from './Header';
import PageLoading from './PageLoading';
import Sidebar from './Sidebar';

import 'dayjs/locale/pt-br';

import 'react-big-calendar/lib/css/react-big-calendar.css';

import './calendar.css';
import LoadingButton from './LoadingButton';

dayjs.locale('pt-BR');
const localizer = dayjsLocalizer(dayjs);

const _messages = {
  week: 'Semana',
  work_week: 'Semana útil',
  day: 'Dia',
  month: 'Mês',
  previous: 'Anterior',
  next: 'Próximo',
  today: `Hoje`,
  agenda: 'Agenda',

  showMore: (total: number) => `+${total} mais`,
};

export type CustomEventPerson = {
  id: number;
  name: string;
  email: string;
  createdAt: string;
  updatedAt: string;
  calendarColor: string | null;
};

type CustomEventCustomer = {
  id: number;
  phone: string;
  name: string;
};

export type CustomEvent = {
  id: number;
  startAt: string;
  endAt: string;
  price: number;
  status: string;
  description: string;
  person: CustomEventPerson | null;
  customer: CustomEventCustomer | null;
  title: string;
  createdAt: string;
  updatedAt: string;
  isPrivate: boolean;
  googleTitle: string | undefined;
  isCustomerLiveConfirmed: boolean;
  isFinished: boolean;
  comission: number | null;
  parseError: string | null;
  partners: { value: number; label: string }[];
};

export default function CustomCalendar(props: any): ReactNode {
  const [searchParams] = useSearchParams();

  return (
    <CustomCalendarComp
      navigate={useNavigate()}
      params={useParams()}
      query={searchParams}
      {...props}
    />
  );
}

class CustomCalendarComp extends Component {
  constructor(
    readonly props: Readonly<{
      children?: ReactNode;
      navigate?: any;
      currentUser?: any;
      params?: any;
      query?: any;
    }>,
  ) {
    super(props);
    this.state = {
      isLoading: true,
      events: [],
      minDate: dayjs().startOf('month').toISOString(),
      maxDate: dayjs().endOf('month').toISOString(),
      isEditing: false,
      currentEvent: undefined,
      currentDate: dayjs().toISOString(),
      currentView: 'month',
      users: [],
      currentEvents: [],
      currentStatuses: [],
      selectedUsers: [],
      groupByPerson: false,
      isSidebarExpanded: false,
      updatingCurrentEvent: false,
    };
  }

  state: Readonly<{
    isLoading: boolean;
    events: CustomEvent[];
    minDate: string;
    maxDate: string;
    isEditing: boolean;
    currentEvent: CustomEvent | undefined;
    currentDate: string;
    currentView: View;
    users: CustomEventPerson[];
    currentEvents: CustomEvent[];
    currentStatuses: string[];
    selectedUsers: CustomEventPerson[];
    groupByPerson: boolean;
    isSidebarExpanded: boolean;
    updatingCurrentEvent: boolean;
  }>;

  async componentDidMount(): Promise<void> {
    let _s = '';
    let _e = '';

    if (this.props.query.get('s') && this.props.query.get('e')) {
      _s = this.props.query.get('s')!;
      _e = this.props.query.get('e')!;

      this.setState({
        minDate: _s,
        maxDate: _e,
      });
    }

    if (this.props.query.get('v')) {
      this.setState({
        currentView: this.props.query.get('v') as View,
      });
    }

    if (this.props.query.get('p')) {
      this.setState({
        selectedUsers: [parseInt(this.props.query.get('p'))],
      });
    }

    await this.getEvents(_s, _e);
    await this.getUsers();
  }

  async setStatusAPI(status: string) {
    if (!this.state.currentEvent) return;

    this.setState({
      updatingCurrentEvent: true,
    });

    await AdminApi.put(
      `/events/${this.state.currentEvent.id}/status/${status}`,
      {},
    );

    this.state.currentEvent.status = status;
    this.setState({
      currentEvent: this.state.currentEvent,
      updatingCurrentEvent: false,
    });
  }

  async onNavigate(date: Date) {
    const _d = dayjs(date);

    const _s = _d.startOf('month').toISOString();
    const _e = _d.add(60, 'days').endOf('month').toISOString();

    this.setState({
      minDate: _s,
      maxDate: _e,
      currentDate: _d.toISOString(),
    });

    if (
      _d.month() !== dayjs(this.state.minDate).month() ||
      _d.year() !== dayjs(this.state.minDate).year()
    ) {
      this.setState({
        loading: true,
      });

      await this.getEvents(_s, _e);
    }
  }

  async getUsers() {
    this.setState({
      isLoading: true,
      users: [],
    });

    const _users = await AdminApi.get(`/users?onlyPro=true`);

    this.setState({
      isLoading: false,
      users: _users,
    });
  }

  getCurrentEvents(
    events?: CustomEvent[],
    statuses?: string[],
    users?: CustomEventPerson[],
  ): CustomEvent[] {
    let _all = events ?? this.state.events;
    const _statuses = statuses ?? this.state.currentStatuses;
    const _users = users ?? this.state.selectedUsers;

    if (_statuses && _statuses.length) {
      _all = _all.filter((e) => _statuses.includes(e.status));
    }

    if (_users && _users.length) {
      _all = _all.filter((e) =>
        _users.map((u) => u.id).includes(e.person?.id ?? 0),
      );
    }

    return _all;
  }

  async getEvents(start?: string, end?: string): Promise<void> {
    this.setState({
      isLoading: true,
      events: [],
    });

    const _s = start || this.state.minDate;
    const _e = end || this.state.maxDate;

    const _events = await AdminApi.get(`/events?s=${_s}&e=${_e}`);

    this.setState({
      isLoading: false,
      events: _events,
      currentEvents: this.getCurrentEvents(_events),
    });
  }

  openEvent(e: CustomEvent) {
    if (e.isPrivate) return;

    this.setState({
      isEditing: true,
      currentEvent: e,
    });
  }

  closeEvent() {
    this.setState({
      isEditing: false,
      currentEvent: undefined,
    });
  }

  getStatusName(e?: CustomEvent): string {
    if (!e) {
      return '';
    }

    switch (e.status) {
      case 'pending':
        return 'Sem sinal';
      case 'confirmed':
        return 'Confirmado com sinal';
      case 'not_executed':
        return 'Não executado';
      default:
        return 'Cancelado';
    }
  }

  getStatusClass(e?: CustomEvent): string {
    if (!e) {
      return '';
    }

    switch (e.status) {
      case 'pending':
        return 'bg-warning';
      case 'confirmed':
        return 'bg-success';
      case 'not_executed':
        return 'bg-secondary';
      default:
        return 'bg-danger';
    }
  }

  getEventColor(e?: CustomEvent): string {
    if (!e) {
      return '';
    }

    if (e.parseError) {
      return '#DC3545';
    }

    return e.person?.calendarColor ?? '';
  }

  formatPrice(e?: CustomEvent): string {
    if (!e) return '';

    return this.formatCurrency(e.price);
  }

  formatCurrency(val: number): string {
    const _d = new Decimal(val);
    return `R$ ${_d.toDecimalPlaces(2).toNumber().toLocaleString('pt-BR', {
      minimumFractionDigits: 2,
    })}`;
  }

  modalEvent() {
    return (
      <div
        className="modal show"
        style={{ display: 'block', position: 'initial' }}
      >
        <Modal
          show={this.state.isEditing}
          onHide={() => {
            this.closeEvent();
          }}
        >
          <Modal.Header closeButton>
            <Modal.Title>{this.state.currentEvent?.title}</Modal.Title>
          </Modal.Header>

          <Modal.Body>
            <div className="list-group">
              <div
                className="list-group-item list-group-item-action"
                aria-current="true"
              >
                <div className="d-flex w-100 justify-content-between">
                  <h5 className="mb-1">Data e Hora</h5>
                </div>
                <p className="mb-1">
                  {dayjs(this.state.currentEvent?.startAt).format('DD/MM/YYYY')}{' '}
                  de {dayjs(this.state.currentEvent?.startAt).format('HH:mm')}{' '}
                  até {dayjs(this.state.currentEvent?.endAt).format('HH:mm')}
                </p>
              </div>
              <div
                className="list-group-item list-group-item-action"
                aria-current="true"
              >
                <div className="d-flex w-100 justify-content-between">
                  <h5 className="mb-1">Profissional</h5>
                </div>
                <p className="mb-1">{this.state.currentEvent?.person?.name} </p>
              </div>
              <div
                className="list-group-item list-group-item-action"
                aria-current="true"
              >
                <div className="d-flex w-100 justify-content-between">
                  <h5 className="mb-1">Cliente</h5>
                </div>
                <p className="mb-1">
                  {this.state.currentEvent?.customer?.name}{' '}
                  {this.state.currentEvent?.customer?.phone}
                </p>
              </div>
              <div className="list-group-item list-group-item-action">
                <div className="d-flex w-100 justify-content-between">
                  <h5 className="mb-1">Status</h5>
                </div>
                <div className="mb-1">
                  {this.state.updatingCurrentEvent ? (
                    <LoadingButton />
                  ) : (
                    <select
                      className="form-select"
                      value={this.state.currentEvent?.status}
                      onChange={(val) => this.setStatusAPI(val.target.value)}
                    >
                      <option value="pending">Sem sinal</option>
                      <option value="confirmed">Confirmado com sinal</option>
                      <option value="not_executed">Não executado</option>
                      <option value="cancelled">Cancelado</option>
                    </select>
                  )}
                </div>
              </div>
              <div className="list-group-item list-group-item-action">
                <div className="d-flex w-100 justify-content-between">
                  <h5 className="mb-1">Valor</h5>
                </div>
                <p className="mb-1">
                  {this.formatPrice(this.state.currentEvent)}

                  {this.state.currentEvent?.comission ? (
                    <p className="text-muted">
                      Comissão Específica:{' '}
                      {this.formatCurrency(this.state.currentEvent.comission)}
                    </p>
                  ) : null}
                </p>
              </div>
              <div className="list-group-item list-group-item-action">
                <div className="d-flex w-100 justify-content-between">
                  <h5 className="mb-1">Descrição</h5>
                </div>
                <p
                  className="mb-1"
                  dangerouslySetInnerHTML={{
                    __html: (
                      this.state.currentEvent?.description ?? 'Sem descrição'
                    ).replace(/\n/g, '<br />'),
                  }}
                ></p>
                <small>
                  Agendado:{' '}
                  {dayjs(this.state.currentEvent?.createdAt).format(
                    'DD/MM/YYYY HH:mm:ss',
                  )}{' '}
                </small>
                <br />
                <small>
                  Atualizado:{' '}
                  {dayjs(this.state.currentEvent?.updatedAt).format(
                    'DD/MM/YYYY HH:mm:ss',
                  )}{' '}
                </small>
                <br />
                <small>
                  Título no Google: {this.state.currentEvent?.googleTitle ?? ''}{' '}
                </small>
              </div>
            </div>
          </Modal.Body>

          <Modal.Footer>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => {
                this.closeEvent();
              }}
            >
              Fechar
            </button>
          </Modal.Footer>
        </Modal>
      </div>
    );
  }

  setStatus(status: string) {
    let _newStatuses = [];

    if (this.state.currentStatuses.includes(status)) {
      _newStatuses = this.state.currentStatuses.filter((s) => s !== status);
    } else {
      _newStatuses = [...this.state.currentStatuses, status];
    }

    this.setState({
      currentStatuses: _newStatuses,
      currentEvents: this.getCurrentEvents(undefined, _newStatuses),
    });
  }

  setUser(user: CustomEventPerson) {
    let _newUsers = [];

    if (this.state.selectedUsers.includes(user)) {
      _newUsers = this.state.selectedUsers.filter((s) => s.id !== user.id);
    } else {
      _newUsers = [...this.state.selectedUsers, user];
    }

    this.setState({
      selectedUsers: _newUsers,
      currentEvents: this.getCurrentEvents(undefined, [], _newUsers),
    });
  }

  putActive(status: string) {
    return this.state.currentStatuses.includes(status) ? 'active' : '';
  }

  statusesButtons() {
    return (
      <div className="row mb-3">
        <div className="col-md-12">
          <button
            type="button"
            className={`btn btn-sm btn-outline-warning me-2 ${this.putActive('pending')}`}
            onClick={() => this.setStatus('pending')}
          >
            Sem sinal
          </button>
          <button
            type="button"
            className={`btn btn-sm btn-outline-success me-2 ${this.putActive('confirmed')}`}
            onClick={() => this.setStatus('confirmed')}
          >
            Confirmado com sinal
          </button>
          <button
            type="button"
            className={`btn btn-sm btn-outline-secondary me-2 ${this.putActive('not_executed')}`}
            onClick={() => this.setStatus('not_executed')}
          >
            Não executado
          </button>
          <button
            type="button"
            className={`btn btn-sm btn-outline-danger me-2 ${this.putActive('cancelled')}`}
            onClick={() => this.setStatus('cancelled')}
          >
            Cancelado
          </button>
        </div>
      </div>
    );
  }

  usersButtons() {
    return (
      <div className="row">
        <div className="col-md-12">
          {this.state.users.map((u) => (
            <button
              key={u.id}
              type="button"
              className={`btn btn-sm btn-link me-2`}
              style={{
                color: u.calendarColor ?? '#000',
                borderColor: this.state.selectedUsers
                  .map((a) => a.id)
                  .includes(u.id)
                  ? (u.calendarColor ?? undefined)
                  : undefined,
              }}
              onClick={() => this.setUser(u)}
            >
              {u.name}
            </button>
          ))}
          <button
            type="button"
            className={`group btn btn-sm btn-outline-primary me-2 ${this.state.groupByPerson ? 'active' : ''}`}
            onClick={() =>
              this.setState({ groupByPerson: !this.state.groupByPerson })
            }
          >
            Agrupar por profissional
          </button>
        </div>
      </div>
    );
  }

  toolbar() {
    return <div>{this.usersButtons()}</div>;
  }

  changeSidebar(expanded: boolean) {
    this.setState({
      isSidebarExpanded: expanded,
    });
  }

  sizeClasses() {
    return this.state.isSidebarExpanded
      ? `col-md-9 col-lg-10`
      : `large-calendar`;
  }

  async googleSync() {
    const _ok = confirm(
      'Deseja sincronizar os eventos com a Google Agenda? Essa ação pode demorar alguns minutos.',
    );

    if (!_ok) return;

    this.setState({
      isLoading: true,
    });

    try {
      await AdminApi.post('/events/googleapi/sync', {});

      await this.getEvents(this.state.minDate, this.state.maxDate);
    } catch (error) {
      console.error(error);
      alert(
        'Erro ao sincronizar com a Google Agenda. Verifique o console para mais detalhes.',
      );
    }

    this.setState({
      isLoading: false,
    });
  }

  render(): ReactNode {
    return this.state.isLoading ? (
      <PageLoading />
    ) : (
      <div>
        <Header
          navigate={this.props.navigate}
          currentUser={this.props.currentUser}
        ></Header>
        <div className="container-fluid">
          {this.state.currentEvent ? this.modalEvent() : null}
          <div className="row">
            <Sidebar
              navigate={this.props.navigate}
              currentUser={this.props.currentUser}
              initialExpanded={false}
              onChangeSidebar={(expanded: boolean) =>
                this.changeSidebar(expanded)
              }
            ></Sidebar>
            <main className={`ms-sm-auto px-md-4 ${this.sizeClasses()}`}>
              <div className="d-flex justify-content-between flex-wrap flex-md-nowrap align-items-center pt-3 pb-2 mb-3 border-bottom">
                <h1 className="h2">Calendário</h1>
              </div>

              <div>
                <div className="d-flex justify-content-between flex-wrap flex-md-nowrap align-items-center pb-2 mb-3 border-bottom">
                  <div className="row">
                    <div className="col-md-12">
                      <button
                        type="button"
                        className="btn btn-primary text-white btn-sm"
                        onClick={() => this.googleSync()}
                      >
                        Sincronizar com a Google Agenda
                      </button>
                    </div>
                  </div>
                </div>
                <div className="mb-3">{this.toolbar()}</div>
                <hr />
              </div>

              <Calendar
                localizer={localizer}
                culture="pt-BR"
                messages={_messages}
                events={this.state.currentEvents}
                date={dayjs(this.state.currentDate).toDate()}
                startAccessor={(e) => dayjs(e.startAt).toDate()}
                endAccessor={(e) => dayjs(e.endAt).toDate()}
                style={{ height: '75vh' }}
                onSelectEvent={(e) => this.openEvent(e)}
                view={this.state.currentView}
                resourceIdAccessor="id"
                resourceTitleAccessor="name"
                resourceAccessor={(e) => e.person!.id}
                resources={
                  this.state.groupByPerson ? this.state.users : undefined
                }
                onNavigate={(date) => {
                  this.onNavigate(date);
                }}
                onView={(view) => {
                  this.setState({
                    currentView: view,
                  });
                }}
                components={{
                  event: (event) => (
                    <div className="event-box">
                      {this.state.currentView != 'agenda' ? (
                        <div
                          className={`event-status-color ${this.getEventColor(event.event)}`}
                        ></div>
                      ) : null}
                      <div
                        className="rbc-event-content"
                        title={
                          this.state.currentView === 'agenda'
                            ? `${event.event.person?.name}: ${event.event.title}`
                            : event.event.title
                        }
                      >
                        {event.event.parseError && (
                          <p>Erro: {event.event.parseError}</p>
                        )}
                        {this.state.currentView === 'agenda'
                          ? `${event.event.person?.name}: ${event.event.title}`
                          : event.event.title}
                      </div>
                    </div>
                  ),
                }}
                eventPropGetter={(event) => {
                  return {
                    // className: 'custom-event-ribbon',
                    style:
                      this.state.currentView !== 'agenda'
                        ? {
                            backgroundColor: this.getEventColor(event),
                          }
                        : undefined,
                  };
                }}
              />
            </main>
          </div>
        </div>
      </div>
    );
  }
}
